<template>
  <div class="modern-calendar">
    <v-card class="calendar-card" elevation="2">
      <!-- Toolbar del Calendario -->
      <v-toolbar flat color="white" class="calendar-toolbar">
        <v-btn icon @click="previous" class="mr-2">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        
        <div class="text-h6 font-weight-bold text-uppercase primary--text">
          {{ currentPeriodLabel }}
        </div>
        
        <v-btn icon @click="next" class="ml-2">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>

        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              outlined 
              color="primary" 
              class="mr-4" 
              @click="today"
              v-bind="attrs"
              v-on="on"
            >
              Hoy
            </v-btn>
          </template>
          <span>Ir a hoy</span>
        </v-tooltip>

        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              depressed
              v-bind="attrs"
              v-on="on"
              class="d-none d-sm-flex"
            >
              <v-icon left>mdi-calendar</v-icon>
              {{ viewLabels[view] }}
              <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-for="(label, key) in viewLabels" :key="key" @click="view = key">
              <v-list-item-title>{{ label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-0 calendar-content">
        
        <!-- VISTA UNIFICADA (Month/Week/Day) -->
        <div v-if="view !== 'list'" class="view-unified fade-transition">
          <!-- Cabecera Días (Solo Month/Week) -->
          <div v-if="view !== 'day'" class="week-header">
            <div v-for="dayName in weekDays" :key="dayName" class="week-day-label">
              {{ dayName }}
            </div>
          </div>
          <!-- Cabecera Días (Solo Day) -->
           <div v-else class="week-header day-view-header">
             <div class="week-day-label">{{ formatDayName(currentDate) }}</div>
          </div>

          <!-- Grid Estático -->
          <div 
             class="unified-grid"
             :class="[`grid-view-${view}`]"
          >
            <div
              v-for="(day, index) in visibleDays"
              :key="day.date.toString()"
              class="calendar-cell"
              :class="{
                'other-month': !day.currentMonth && view === 'month',
                'today': day.isToday,
                'selected': isSelected(day.date),
                'cell-week-mode': view === 'week',
                'cell-day-mode': view === 'day'
              }"
              @click="selectDate(day.date)"
            >
              <div class="cell-header">
                <span class="day-number" :class="{ 'day-number-today': day.isToday }">
                  {{ day.dayNumber }}
                </span>
              </div>
              
              <!-- Contenedor de eventos con scroll si son muchos -->
              <div class="cell-events-container custom-scrollbar">
                <div 
                  v-for="event in day.events" 
                  :key="event.id"
                  class="event-chip"
                  :class="getEventClass(event)"
                  @click.stop="selectEvent(event)"
                  v-ripple
                >
                  <div class="d-flex align-center">
                      <span class="event-time mr-1">{{ formatTime(event.start) }}</span>
                      <span class="event-title text-truncate">{{ event.title }}</span>
                  </div>
                  <div v-if="view !== 'month'" class="caption grey--text text--lighten-4 text-truncate">
                      {{ event.description }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- VISTA LISTA -->
        <div v-if="view === 'list'" class="view-list fade-transition">
           <div v-if="sortedEvents.length === 0" class="pa-8 text-center grey--text">
              <v-icon size="64" color="grey lighten-2">mdi-calendar-blank</v-icon>
              <div class="mt-2">No hay eventos para este período</div>
           </div>
           
           <v-list two-line>
              <template v-for="(group, dateKey) in eventsGroupedByDate">
                 <v-subheader :key="'header-' + dateKey" class="grey lighten-4 font-weight-bold">
                    {{ formatDateFull(dateKey) }}
                 </v-subheader>
                 <v-list-item
                    v-for="event in group"
                    :key="event.id"
                    @click="selectEvent(event)"
                    class="event-list-item"
                 >
                    <v-list-item-avatar>
                       <v-icon :class="getEventColorClass(event)" dark class="white--text rounded-circle pa-2">
                          {{ getEventIcon(event) }}
                       </v-icon>
                    </v-list-item-avatar>
                    
                    <v-list-item-content>
                       <v-list-item-title class="font-weight-medium">{{ event.title }}</v-list-item-title>
                       <v-list-item-subtitle>
                          <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                          {{ formatTime(event.start) }} - {{ formatTime(event.end) }}
                          <span v-if="event.description" class="ml-2 hidden-sm-and-down">
                            — {{ event.description }}
                          </span>
                       </v-list-item-subtitle>
                    </v-list-item-content>
                    
                    <v-list-item-action>
                       <v-chip x-small :class="getEventStatusColor(event.status)" outlined>
                          {{ getEventStatusLabel(event.status) }}
                       </v-chip>
                    </v-list-item-action>
                 </v-list-item>
                 <v-divider :key="'div-'+dateKey"></v-divider>
              </template>
           </v-list>
        </div>
      </v-card-text>

      <!-- FAB para móvil -->
      <v-fab-transition>
        <v-btn
          v-if="$vuetify.breakpoint.smAndDown"
          color="primary"
          fab
          fixed
          bottom
          right
          @click="addEvent"
          class="mb-12" 
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-card>
  </div>
</template>

<script>
import { 
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
  eachDayOfInterval, isSameMonth, isToday, isSameDay, 
  addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  startOfDay, endOfDay
} from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'ModernCalendar',
  props: {
    events: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentDate: new Date(),
      view: 'month', // 'month', 'week', 'day', 'list'
      selectedDate: null,
      weekDays: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
      viewLabels: {
        'month': 'Mes',
        'week': 'Semana',
        'day': 'Día',
        'list': 'Agenda'
      },
    }
  },
  computed: {
    currentPeriodLabel() {
      if (this.view === 'month') {
        return format(this.currentDate, 'MMMM yyyy', { locale: es })
      } else if (this.view === 'week') {
        const start = startOfWeek(this.currentDate, { weekStartsOn: 0 })
        const end = endOfWeek(this.currentDate, { weekStartsOn: 0 })
        if (isSameMonth(start, end)) {
           return `${format(start, 'd', { locale: es })} - ${format(end, 'd', { locale: es })} de ${format(start, 'MMMM', { locale: es })}`
        }
        return `${format(start, 'd MMM', { locale: es })} - ${format(end, 'd MMM', { locale: es })}`
      } else if (this.view === 'day') {
        return format(this.currentDate, "EEEE d 'de' MMMM", { locale: es })
      } else {
        return 'Agenda' // Lista
      }
    },
    
    // Propiedad unificada para los días a mostrar
    visibleDays() {
      let days = []
      
      if (this.view === 'month') {
          const start = startOfMonth(this.currentDate)
          const end = endOfMonth(this.currentDate)
          const startCal = startOfWeek(start, { weekStartsOn: 0 })
          const endCal = endOfWeek(end, { weekStartsOn: 0 })
          days = eachDayOfInterval({ start: startCal, end: endCal })
      } else if (this.view === 'week') {
          const start = startOfWeek(this.currentDate, { weekStartsOn: 0 })
          const end = endOfWeek(this.currentDate, { weekStartsOn: 0 })
          days = eachDayOfInterval({ start, end })
      } else if (this.view === 'day') {
          days = [this.currentDate]
      }

      return days.map(day => ({
        date: day,
        dayNumber: day.getDate(),
        currentMonth: isSameMonth(day, this.currentDate),
        isToday: isToday(day),
        // Ordenar eventos por hora
        events: this.getEventsForDate(day).sort((a,b) => new Date(a.start) - new Date(b.start))
      }))
    },

    sortedEvents() {
      return [...this.events].sort((a, b) => new Date(a.start) - new Date(b.start))
    },

    eventsGroupedByDate() {
      const grouped = {}
      this.sortedEvents.forEach(event => {
        const dateKey = format(new Date(event.start), 'yyyy-MM-dd')
        if (!grouped[dateKey]) grouped[dateKey] = []
        grouped[dateKey].push(event)
      })
      // Ordenar claves
      return Object.keys(grouped).sort().reduce((obj, key) => { 
          obj[key] = grouped[key]; 
          return obj;
      }, {});
    }
  },
  watch: {
    '$vuetify.breakpoint.name'(val) {
       // Auto switch a lista en movil si es necesario
       if (val === 'xs' || val === 'sm') {
         if (this.view !== 'day' && this.view !== 'list') {
            this.view = 'list'
         }
       }
    }
  },
  mounted() {
    if (this.$vuetify.breakpoint.smAndDown) {
      this.view = 'list'
    }
  },
  methods: {
    previous() {
      if (this.view === 'month') this.currentDate = subMonths(this.currentDate, 1)
      else if (this.view === 'week') this.currentDate = subWeeks(this.currentDate, 1)
      else if (this.view === 'day') this.currentDate = subDays(this.currentDate, 1)
    },
    next() {
      if (this.view === 'month') this.currentDate = addMonths(this.currentDate, 1)
      else if (this.view === 'week') this.currentDate = addWeeks(this.currentDate, 1)
      else if (this.view === 'day') this.currentDate = addDays(this.currentDate, 1)
    },
    today() {
      this.currentDate = new Date()
    },
    isSelected(date) {
      return this.selectedDate && isSameDay(date, this.selectedDate)
    },
    selectDate(date) {
      this.selectedDate = date
      this.$emit('date-selected', date)
    },
    selectEvent(event) {
      this.$emit('event-selected', event)
    },
    addEvent() {
      this.$emit('event-added', { date: this.currentDate })
    },
    
    // Helpers formato
    formatTime(dateStr) {
      return format(new Date(dateStr), 'HH:mm')
    },
    formatDateFull(dateStr) {
       const [y, m, d] = dateStr.split('-')
       const date = new Date(y, m-1, d)
       return format(date, "EEEE d 'de' MMMM", { locale: es })
    },
    formatDayName(date) {
       return format(date, 'EEEE', { locale: es }).toUpperCase()
    },

    getEventsForDate(date) {
      return this.events.filter(event => isSameDay(new Date(event.start), date))
    },

    // Estilos de eventos
    getEventClass(event) {
      const status = event.status ? event.status.toLowerCase() : 'confirmed'
      return `event-${status}`
    },
    
    getEventStatusColor(status) {
       const colors = {
          'free': 'green--text',
          'confirmed': 'blue--text',
          'checked_in': 'orange--text',
          'done': 'grey--text',
          'cancelled': 'red--text'
       }
       return colors[status?.toLowerCase()] || 'grey--text'
    },
    getEventStatusLabel(status) {
        const labels = {
            'free': 'Libre',
            'confirmed': 'Confirmado',
            'checked_in': 'En Espera',
            'done': 'Terminado',
            'cancelled': 'Cancelado'
        }
        return labels[status?.toLowerCase()] || status
    },
    getEventIcon(event) {
       if (event.type === 'consultation') return 'mdi-doctor'
       return 'mdi-calendar-check'
    },
    getEventColorClass(event) {
       const status = event.status ? event.status.toLowerCase() : 'confirmed'
       const map = {
          'free': 'green',
          'confirmed': 'blue',
          'checked_in': 'orange',
          'done': 'grey darken-1',
          'cancelled': 'red'
       }
       return map[status] || 'primary'
    }
  }
}
</script>

<style scoped>
.modern-calendar {
  height: 100%;
  padding: 8px;
  background-color: #f5f7fa; 
}

.calendar-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.calendar-content {
  flex: 1;
  overflow: hidden; /* No scroll en el contenedor padre */
  position: relative;
  /* Altura mínima */
  min-height: 500px;
  max-height: calc(100vh - 200px); /* Evita que crezca indefinidamente */
}

/* UNIFIED VIEW STYLES */

.view-unified {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%; /* No crecer más allá del contenedor */
  overflow: hidden; /* Evita scroll externo */
}

.week-header {
  display: grid;
  /* El template column se alineará con el grid principal */
  grid-template-columns: repeat(7, 1fr); 
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.week-day-label {
  padding: 8px;
  text-align: center;
  font-weight: 600;
  color: #757575;
  font-size: 0.85rem;
}

.day-view-header {
  grid-template-columns: 1fr !important;
}

.unified-grid {
  display: grid;
  flex: 1; /* Ocupar el resto del espacio vertical */
  overflow-y: auto; /* Scroll solo en el grid si es necesario */
  max-height: 100%; /* No exceder el contenedor padre */
}

/* Modifier classes for grid columns */
.grid-view-month, .grid-view-week {
  grid-template-columns: repeat(7, 1fr);
  /* Rows con altura fija en vez de 1fr para evitar expansión */
  grid-auto-rows: minmax(120px, auto); 
}

.grid-view-day {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr; /* Full height */
}

/* CELL STYLES */
.calendar-cell {
  border-right: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 4px;
  background: white;
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 120px; /* Altura mínima consistente */
  max-height: 120px; /* Altura máxima fija en vista mensual */
  overflow: hidden; /* Evita que el contenido desborde */
}

.calendar-cell:hover {
  background: #fdfdfd;
}
.calendar-cell.other-month {
  background: #fafafa;
  color: #bdbdbd;
}
.calendar-cell.today {
  background: #f0f7ff;
}

/* Week/Day mode specific adjustments */
.cell-week-mode {
  min-height: 400px; /* Ensure week days are tall enough */
  max-height: none; /* Permitir expansión en vista semanal */
}

.cell-day-mode {
  height: 100%; /* Fill container */
  min-height: 400px;
  max-height: none; /* Permitir expansión en vista diaria */
}

.cell-header {
  text-align: right;
  margin-bottom: 4px;
  flex-shrink: 0;
}
.day-number {
  display: inline-block;
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  border-radius: 50%;
  font-size: 0.9rem;
}
.day-number-today {
  background: #1976d2;
  color: white;
  font-weight: bold;
}

/* Events Container */
.cell-events-container {
  flex: 1;
  overflow-y: auto; /* Scroll interno si hay muchos eventos */
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* SCROLLBAR */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}

/* EVENT CHIP */
.event-chip {
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: white;
  cursor: pointer;
}
.event-chip:hover {
  filter: brightness(0.95);
}
.event-time { font-weight: bold; font-size: 0.75rem; white-space: nowrap; }

/* STATUS COLORS */
.event-free { background-color: #4CAF50; border-left: 3px solid #2E7D32; }
.event-confirmed { background-color: #2196F3; border-left: 3px solid #1565C0; }
.event-checked_in { background-color: #FF9800; border-left: 3px solid #EF6C00; }
.event-done { background-color: #9E9E9E; border-left: 3px solid #616161; }
.event-cancelled { background-color: #F44336; border-left: 3px solid #C62828; }

.fade-transition {
  animation: fadeIn 0.3s ease;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Mobile Tweaks */
@media (max-width: 600px) {
    .calendar-toolbar .v-btn {
        padding: 0 8px;
    }
}
</style>